import find from 'lodash/find';
import invert from 'lodash/invert';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import Utils from '../utils';
import { Capability } from './account';
import BarcodeTypes from './barcodes/BarcodeTypes.enum';
import { AllCarriers, Carrier } from './carriers';
import { DeliveryConfirmationOptions } from './deliveryConfirmation';
import { InsuranceProvider } from './insurance';
import { PrintOnEnum, PrintOnOptions } from './printOn';
import States from './States';
import {
	Service,
	ServiceType,
	ServiceUtil,
	ShipmentContents,
} from './services';

const { Session, Util } = Utils;
class CalculatedData {
	// build array of abbreviation using States static data
	static abbrStates = States.map(state => state.abbreviation);

	// build carriers map object using AllCarriers static data
	static allCarriersMap = {};

	static allServices = [];

	static allServicesMap = {};

	static allShippingOptionsMap = {};

	/**
	 * Building is dependent on Session accountInfo object, hence, call init() when
	 * Session data is loaded.
	 */
	static init = params => {
		const carrierCapabilities = Session.getCarrierCapabilities();
		const { shippingOptions } = params || {};

		// check for service override based on user's PostalOverride app cap
		const postalOverrideAppCap = Session.hasCapability(
			Capability.PostalOverride
		);

		// initialize ServiceUtil class
		this.allShippingOptionsMap = ServiceUtil.init(shippingOptions);

		// build all services list using AllServices static data
		this.allServices = ServiceUtil.buildAllServices(postalOverrideAppCap);

		// build the map object using allServices array
		this.allServicesMap = this.allServices.reduce(
			(_allServicesMap, servicePackage) => ({
				..._allServicesMap,
				[servicePackage.key]: servicePackage,
			}),
			{}
		);

		// build all configured carriers
		this.allCarriers = AllCarriers.reduce((_allCarriers, carrier) => {
			if (carrierCapabilities[carrier.value] === true) {
				_allCarriers.push(carrier);
			}

			return _allCarriers;
		}, []);

		// build carriers map object
		this.allCarriersMap = this.allCarriers.reduce((_carriersMap, carrier) => {
			_carriersMap[carrier.key] = carrier;
			return _carriersMap;
		}, {});
	};

	/**
	 * Return the service static data.
	 * @param {ServiceType} serviceType
	 * @param {PacakgeType} PackageType
	 * @returns {object|null}
	 */
	static getService = (serviceType, packageType) => {
		// get the Service static data
		const serviceKeyDictionary = invert(Service);
		const serviceKey = serviceKeyDictionary[`${serviceType}_${packageType}`];

		return isObject(CalculatedData.allServicesMap[serviceKey])
			? CalculatedData.allServicesMap[serviceKey]
			: null;
	};

	static getBarcodeType = barcode => {
		const code = barcode.toLowerCase();
		if (code.length === 10) {
			if (code.startsWith('x') || code.startsWith('b')) {
				return BarcodeTypes.ASIN;
			} else {
				return BarcodeTypes.ISBN;
			}
		} else if (code.length === 13) {
			return BarcodeTypes.EAN;
		} else {
			return BarcodeTypes.UPC;
		}
	};

	/**
	 * Return service's shipping options data from the carrier-service API.
	 *
	 * @param {ServiceType} serviceType Service type.
	 * @param {PackageType} packageType Package type.
	 * @param {ShipmentContents} shipmentContents Shipment contents (Optional).
	 * @returns {object}
	 */
	static getShippingOptionsData = (
		serviceType,
		packageType,
		shipmentContents
	) => {
		if (isString(serviceType) && isString(packageType)) {
			const serviceShippingOption = isObject(
				this.allShippingOptionsMap[serviceType]
			)
				? this.allShippingOptionsMap[serviceType]
				: {};

			const packageShippingOption = isObject(serviceShippingOption.packages)
				? serviceShippingOption.packages?.package
				: {};

			const contentOptions = isObject(packageShippingOption?.contentOptions)
				? packageShippingOption.contentOptions
				: {};

			/**
			 * Dev note:
			 * Most services have "all" shipment-contents except some international services in which
			 * they have "documents" and "non_documents" shipment-contents.
			 *
			 * Specify shipment-contents in the third arguement, otherwise this method shipment-contents
			 * in the following order:
			 *   1: "all"
			 *   2: "documents"
			 *   3: "non_documents".
			 */
			if (isString(shipmentContents)) {
				return isObject(contentOptions[shipmentContents])
					? contentOptions[shipmentContents]
					: {};
			}

			if (isObject(contentOptions[ShipmentContents.All])) {
				return contentOptions[ShipmentContents.All];
			} else if (isObject(contentOptions[ShipmentContents.Documents])) {
				return contentOptions[ShipmentContents.Documents];
			} else if (isObject(contentOptions[ShipmentContents.NonDocuments])) {
				return contentOptions[ShipmentContents.NonDocuments];
			}
		}

		return {};
	};

	/**
	 * Return the delivery confirmation options.
	 *
	 * @param {ServiceType} serviceType
	 * @param {PackageType} packageType
	 * @param {ShipmentContents} shipmentContents Shipment contents (Optional).
	 * @returns {array}
	 */
	static getDeliveryConfirmationOptions = (
		serviceType,
		packageType,
		contentType
	) => {
		// get service's shipping options
		const shippingOptions = this.getShippingOptionsData(
			serviceType,
			packageType,
			contentType
		);

		return reduce(
			shippingOptions.deliveryConfirmationOptions,
			(_options, deliveryConfirmationOption) => {
				const { deliveryConfirmationType, deliveryConfirmationPrice } =
					deliveryConfirmationOption;

				// look for the static data
				const confirmationOption = find(DeliveryConfirmationOptions, [
					'value',
					deliveryConfirmationType,
				]);

				if (confirmationOption) {
					_options.push({
						...confirmationOption,

						// combine the delivery confirmation price and the static data
						rate: isNumber(deliveryConfirmationPrice)
							? deliveryConfirmationPrice
							: 0,
					});
				}

				return _options;
			},
			[]
		);
	};

	/**
	 * Return the insurance options based on the selected service.
	 *
	 * @param {ServiceType} serviceType
	 * @param {PackageType} packageType
	 * @returns {array}
	 */
	static getInsuranceOptions = (serviceType, packageType) => {
		// get service's shipping options
		const shippingOptions = this.getShippingOptionsData(
			serviceType,
			packageType
		);

		return reduce(
			shippingOptions.insuranceOptions,
			(_options, insuranceOption) => {
				const { insuranceProvider, maxInsuranceValueLimit, currency } =
					insuranceOption;

				if (
					insuranceProvider === InsuranceProvider.ParcelGuard ||
					insuranceProvider === InsuranceProvider.Carrier ||
					insuranceProvider === InsuranceProvider.Stamps
				) {
					_options.push({
						insuranceProvider,
						maxInsuranceValueLimit,
						currency,
					});
				}

				return _options;
			},
			[]
		);
	};

	/**
	 * Return the insurance option based on the selected service.
	 * @param {ServiceType} serviceType
	 * @param {PackageType} packageType
	 * @returns {object}
	 */
	static getInsuranceOption = (serviceType, packageType) => {
		// get insurance options based on selected service
		const insuranceOptions = this.getInsuranceOptions(serviceType, packageType);

		if (isArray(insuranceOptions)) {
			if (insuranceOptions.length === 1) {
				return insuranceOptions[0];
			} else if (insuranceOptions.length > 1) {
				/**
				 * Dev note:
				 * Per Andrew, insurance provider is based on available insurance options from carrier-services
				 * api based on the selected service. In a case where the api returns multiple insurance options,
				 * the priority to choose is:
				 * 1. parcel_guard
				 * 2. carrier
				 * 3. stamps_com, (this will be retired soon)
				 */
				return insuranceOptions.reduce((_optoin, insuranceOption) => {
					const { insuranceProvider } = insuranceOption;

					if (!_optoin) {
						if (insuranceProvider === InsuranceProvider.ParcelGuard) {
							return insuranceOption;
						} else if (insuranceProvider === InsuranceProvider.Carrier) {
							return insuranceOption;
						} else if (insuranceProvider === InsuranceProvider.Stamps) {
							return insuranceOption;
						}
					}

					return _optoin;
				}, null);
			}
		}

		return null;
	};

	/**
	 * Return the print on options.
	 * @returns {array}
	 */
	static getPrintOnOptions = () => {
		const { info = {} } = Session.getAccountInfo();
		const capabilities = isObject(info.Capabilities) ? info.Capabilities : {};

		return PrintOnOptions.map(option => {
			switch (option.value) {
				case PrintOnEnum.NetStamps:
					return {
						...option,
						disabled: capabilities.CanPrintNetStamps !== true,
					};
				case PrintOnEnum.ShippingLabel812x11:
				case PrintOnEnum.ShippingLabel1200:
				case PrintOnEnum.ShippingLabel512x812:
				case PrintOnEnum.Roll4x6:
				case PrintOnEnum.Roll418x614:
				case PrintOnEnum.Roll2x7:
					return {
						...option,
						disabled: !(
							capabilities.CanPrintShipping === true ||
							capabilities.CanPrintAllIndiciumValues === true
						),
					};
				case PrintOnEnum.EnvelopeA2:
				case PrintOnEnum.EnvelopeA9:
				case PrintOnEnum.Envelope6:
				case PrintOnEnum.Envelope7:
				case PrintOnEnum.Envelope9:
				case PrintOnEnum.Envelope10:
				case PrintOnEnum.Envelope11:
				case PrintOnEnum.Envelope12:
				case PrintOnEnum.MailingLabelSDC3110:
				case PrintOnEnum.MailingLabelSDC2200:
				case PrintOnEnum.MailingLabelSDC5030:
					return {
						...option,
						disabled: !(
							capabilities.IsIBIPEnabled === true ||
							capabilities.CanPrintAllIndiciumValues === true
						),
					};
				case PrintOnEnum.MailingLabelCM3610:
				case PrintOnEnum.MailingLabelCM3710:
				case PrintOnEnum.MailingLabelCM3910:
				case PrintOnEnum.MailingLabelCM3930:
				case PrintOnEnum.EnvelopeCM3810:
				case PrintOnEnum.EnvelopeCM3830:
					return {
						...option,
						disabled: !(
							capabilities.CanUseCertifiedMail === true ||
							capabilities.CanPrintAllIndiciumValues === true
						),
					};
				default:
					return {
						...option,
						disabled: false,
					};
			}
		});
	};

	/**
	 * Process the response from /rates api and merge the rates into staticData.
	 * @param {array} serviceRates Data response received from rates api.
	 * @returns {array}
	 */
	static processRates = (rates, upsGroundETA) => {
		const carrierCapabilities = Session.getCarrierCapabilities();
		const serviceRates = Util.toCamelCase(rates);

		return reduce(
			serviceRates,
			(_result, rate) => {
				// get the Service static data
				const serviceStaticData = this.getService(
					rate.serviceType,
					rate.packagingType
				);

				/**
				 * Dev note:
				 * Exclude the following rates:
				 * 1. Any service that is not included in Data module
				 * 2. Any service that is not enabled for user's account
				 */
				if (
					!isObject(serviceStaticData) ||
					(serviceStaticData.carrier === Carrier.UPS &&
						!carrierCapabilities[Carrier.UPS]) ||
					(serviceStaticData.carrier === Carrier.DHLExpress &&
						!carrierCapabilities[Carrier.DHLExpress]) ||
					(serviceStaticData.carrier === Carrier.GlobalPost &&
						!carrierCapabilities[Carrier.GlobalPost])
				) {
					return _result;
				}

				// e.g.
				// estimatedDeliveryDays = "1-2" => [1, 2]
				// estimatedDeliveryDays = "1"   => [1, undefined]
				// estimatedDeliveryDays = null  => [1, 999]
				const estimatedDeliveryDays = isString(rate.estimatedDeliveryDays)
					? rate.estimatedDeliveryDays.match(/\d+/g)?.map(Number)
					: [1, 999];

				let [minDeliverDays, maxDeliverDays] = isArray(estimatedDeliveryDays)
					? estimatedDeliveryDays
					: [];

				// update estimated delivery days of 'ups ground'
				if (rate.serviceType === ServiceType.UpsGround && upsGroundETA) {
					rate.estimatedDeliveryDays =
						isString(upsGroundETA) && upsGroundETA !== ''
							? upsGroundETA
							: rate.estimatedDeliveryDays;
					minDeliverDays = parseInt(upsGroundETA);
					maxDeliverDays = null;
				}

				_result.push({
					// service static data
					...serviceStaticData,

					// service rate data from server
					...rate,

					// service ETA
					minDeliverDays,
					maxDeliverDays,
				});

				return _result;
			},
			[]
		);
	};
}

export default CalculatedData;
